import React from 'react';
import P from 'prop-types';
import axios from 'axios';
import { toSecureApiUri, toInsecureApiUri } from '../utils/url';
import Raven from 'raven-js';

export const AuthContext = React.createContext();

export const loginType = {
   limited: 'Limited',
   verified: 'Verified'
};

export const verifyStatus = {
   success: 0,
   invalidCode: 1,
   exceededAttempts: 2,
   notFound: 3,
   error: 4
};

export const withAuth = Component =>
   class WithAuth extends React.Component {
      render() {
         return (
            <AuthContext.Consumer>
               {value => <Component {...this.props} auth={value} />}
            </AuthContext.Consumer>
         );
      }
   };

export class AuthProvider extends React.Component {
   static propTypes = {
      children: P.node
   };

   constructor(props) {
      super(props);
      let timeoutToken;
      const expiration =
         sessionStorage.getItem('expiration') && Date.parse(sessionStorage.getItem('expiration'));
      const user =
         sessionStorage.getItem('user') && expiration > new Date()
            ? JSON.parse(sessionStorage.getItem('user'))
            : undefined;
      const verificationSkipped =
         sessionStorage.getItem('verificationSkipped') &&
         JSON.parse(sessionStorage.getItem('verificationSkipped'));

      if (user) {
         timeoutToken = setTimeout(this.logout, expiration - new Date());
         Raven.setUserContext({ account: user.account, loginType: user.loginType });
         axios.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
      }
      this.state = {
         timeoutToken,
         user: user,
         verificationSkipped: verificationSkipped,
         login: this.login,
         verify: this.verify,
         logout: this.logout,
         skipVerification: this.skipVerification
      };
   }

   login = credentials => {
      this.setState({ authenticating: true });
      const endpoint = credentials.verify
         ? toSecureApiUri('verify/login')
         : toInsecureApiUri('login');

      return axios
         .post(endpoint, {
            sitePrefix: window.location.hostname.split('.')[0],
            dateOfBirth: credentials.dob,
            accountNumber: credentials.account,
            captchaToken: credentials.captcha,
            verify: credentials.verify
         })
         .then(response => {
            const {
               verifyStatus: verifyStatusResponse,
               expirationMinutes,
               ...userResponse
            } = response.data;

            if (
               userResponse.loginType === loginType.limited ||
               verifyStatusResponse === verifyStatus.success
            ) {
               const timeout = (expirationMinutes - 1) * 60000;
               const user = {
                  account: credentials.account,
                  dob: credentials.dob,
                  ...userResponse
               };

               Raven.setUserContext({ account: user.account, loginType: user.loginType });
               axios.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
               sessionStorage.setItem('user', JSON.stringify(user));
               sessionStorage.setItem(
                  'expiration',
                  new Date(new Date().getTime() + timeout).toISOString()
               );
               const timeoutToken = setTimeout(this.logout, timeout);
               this.setState({ user, timeoutToken, error: undefined });
            }
            return response.data;
         })
         .catch(error => this.setState({ error }))
         .finally(() => this.setState({ authenticating: false }));
   };

   verify = verification => this.login({ ...this.state.user, verify: verification });

   logout = () => {
      Raven.setUserContext();
      axios.defaults.headers.common['Authorization'] = undefined;
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('expiration');
      sessionStorage.removeItem('verificationSkipped');
      clearTimeout(this.state.timeoutToken);
      this.setState({ user: undefined, verificationSkipped: false, timeoutToken: undefined });
   };

   skipVerification = () => {
      this.setState({ verificationSkipped: true });
      sessionStorage.setItem('verificationSkipped', true);
   };

   render() {
      return <AuthContext.Provider value={this.state}>{this.props.children}</AuthContext.Provider>;
   }
}



// WEBPACK FOOTER //
// ./src/contexts/AuthContext.js